import React, { useState, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { ApolloError } from "@apollo/client";
import { PermitType } from "common/types/permits";
import { RegisteredVehicle } from "common/graphql/fragments";
import { VehicleSelectionCard, VehicleSelectionCardSkeleton } from "../VehicleSelectionCard";
import { AddVehicleForm } from "../AddVehicleForm";

export interface SelectVehiclesStepContentProps {
  /** The selected permit type */
  permitType: PermitType | null;
  /** List of user's registered vehicles */
  vehicles: RegisteredVehicle[];
  /** Whether vehicles are loading */
  loading: boolean;
  /** Error from vehicle fetch */
  error?: ApolloError;
  /** IDs of selected vehicles */
  selectedVehicleIds: string[];
  /** Whether to apply permit to all vehicles */
  applyToAllVehicles: boolean;
  /** Whether to auto-assign new vehicles */
  autoAssignNewVehicles: boolean;
  /** Handler for selection changes */
  onSelectionChange: (selectedIds: string[], applyToAll: boolean, autoAssign: boolean) => void;
  /** Handler called when a vehicle is added */
  onVehicleAdded: (vehicleId: string) => void;
  /** Handler to retry fetching vehicles on error */
  onRetry?: () => void;
}

/**
 * Presentation component for the Select Vehicles step.
 * Handles both per-vehicle and per-account permit types.
 */
export const SelectVehiclesStepContent: React.FC<SelectVehiclesStepContentProps> = ({
  permitType,
  vehicles,
  loading,
  error,
  selectedVehicleIds,
  applyToAllVehicles,
  autoAssignNewVehicles,
  onSelectionChange,
  onVehicleAdded,
  onRetry,
}) => {
  const [isAddVehicleOpen, setIsAddVehicleOpen] = useState(false);
  const [noAutoAssignAcknowledged, setNoAutoAssignAcknowledged] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredVehicles = useMemo(() => {
    if (!searchQuery.trim()) {
      return vehicles;
    }
    const query = searchQuery.toLowerCase().trim();
    return vehicles.filter(
      (v) =>
        v.licensePlate?.toLowerCase().includes(query) ||
        v.make?.toLowerCase().includes(query) ||
        v.model?.toLowerCase().includes(query),
    );
  }, [vehicles, searchQuery]);

  const isPerAccountPermit = permitType?.vehicleScope === "per_account";
  const showAutoAssignSection = permitType?.isAutomaticVehicleAssignmentEnabled ?? false;

  const selectedCount = applyToAllVehicles ? vehicles.length : selectedVehicleIds.length;

  const handleApplyToAllChange = useCallback(
    (checked: boolean) => {
      onSelectionChange(checked ? vehicles.map((v) => v.id) : [], checked, autoAssignNewVehicles);
    },
    [vehicles, autoAssignNewVehicles, onSelectionChange],
  );

  const handleVehicleToggle = useCallback(
    (vehicleId: string) => {
      const isSelected = selectedVehicleIds.includes(vehicleId);
      const newSelectedIds = isSelected
        ? selectedVehicleIds.filter((id) => id !== vehicleId)
        : [...selectedVehicleIds, vehicleId];
      onSelectionChange(newSelectedIds, false, autoAssignNewVehicles);
    },
    [selectedVehicleIds, autoAssignNewVehicles, onSelectionChange],
  );

  const handleAutoAssignChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        setNoAutoAssignAcknowledged(false);
      }
      onSelectionChange(selectedVehicleIds, applyToAllVehicles, checked);
    },
    [selectedVehicleIds, applyToAllVehicles, onSelectionChange],
  );

  const handleNoAutoAssignChange = useCallback(
    (checked: boolean) => {
      setNoAutoAssignAcknowledged(checked);
      if (checked) {
        onSelectionChange(selectedVehicleIds, applyToAllVehicles, false);
      }
    },
    [selectedVehicleIds, applyToAllVehicles, onSelectionChange],
  );

  const handleVehicleAdded = useCallback(
    (vehicleId: string) => {
      setIsAddVehicleOpen(false);
      onVehicleAdded(vehicleId);
    },
    [onVehicleAdded],
  );

  // Per-Account Permit: Show info message only
  if (isPerAccountPermit) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Vehicle Coverage
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your permit coverage scope
        </Typography>

        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ borderRadius: 2 }}>
          <AlertTitle>Account-Wide Permit</AlertTitle>
          This permit automatically applies to all vehicles in your account. No vehicle selection is needed.
        </Alert>
      </Box>
    );
  }

  // Per-Vehicle Permit: Show full selection UI
  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Select Vehicles
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Select which vehicles this permit will cover
      </Typography>

      {/* Apply to All Checkbox */}
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          p: 2,
          mb: 3,
          bgcolor: applyToAllVehicles ? "primary.50" : "grey.50",
          borderColor: applyToAllVehicles ? "primary.main" : "divider",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={applyToAllVehicles}
              onChange={(e) => handleApplyToAllChange(e.target.checked)}
              disabled={loading}
            />
          }
          label={<Typography fontWeight={500}>Apply permit to all vehicles in my fleet</Typography>}
        />
      </Card>

      {/* Error State */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            onRetry && (
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            )
          }
        >
          <AlertTitle>Error Loading Vehicles</AlertTitle>
          {error.message || "Failed to load your vehicles. Please try again."}
        </Alert>
      )}

      {/* Vehicle List */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
          Select Vehicles *
        </Typography>

        {/* Search field — only shown when there are many vehicles */}
        {vehicles.length > 10 && (
          <TextField
            size="small"
            placeholder="Search by plate, make, or model..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5, width: "100%" }}
          />
        )}

        {loading ? (
          // Loading skeletons
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <VehicleSelectionCardSkeleton key={index} />
            ))}
          </Box>
        ) : vehicles.length === 0 ? (
          // Empty state
          <Card variant="outlined" sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">No vehicles registered yet. Add your first vehicle below.</Typography>
          </Card>
        ) : filteredVehicles.length === 0 ? (
          // No search results
          <Card variant="outlined" sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">No vehicles match your search.</Typography>
          </Card>
        ) : (
          // Virtualized vehicle cards
          <FixedSizeList
            height={Math.min(filteredVehicles.length * 92, 400)}
            width="100%"
            itemSize={92}
            itemCount={filteredVehicles.length}
            overscanCount={5}
          >
            {({ index, style }: ListChildComponentProps) => {
              const vehicle = filteredVehicles[index];
              return (
                <div style={{ ...style, paddingRight: 8 }}>
                  <VehicleSelectionCard
                    vehicle={vehicle}
                    isSelected={applyToAllVehicles || selectedVehicleIds.includes(vehicle.id)}
                    isDisabled={applyToAllVehicles}
                    onToggle={handleVehicleToggle}
                  />
                </div>
              );
            }}
          </FixedSizeList>
        )}
      </Box>

      {/* Selected Count */}
      <Card variant="outlined" sx={{ p: 2, mb: 3, bgcolor: "grey.50" }}>
        <Typography variant="body2" color="text.secondary">
          Selected: <strong>{selectedCount}</strong> vehicle(s)
        </Typography>
      </Card>

      {/* Add Vehicle Section */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => setIsAddVehicleOpen(!isAddVehicleOpen)}
          startIcon={isAddVehicleOpen ? <ExpandLessIcon /> : <AddIcon />}
          sx={{
            borderStyle: "dashed",
            py: 1.5,
          }}
        >
          {isAddVehicleOpen ? "Cancel" : "Add New Vehicle"}
        </Button>
        <Collapse in={isAddVehicleOpen}>
          <Box sx={{ mt: 2 }}>
            <AddVehicleForm onVehicleAdded={handleVehicleAdded} onCancel={() => setIsAddVehicleOpen(false)} />
          </Box>
        </Collapse>
      </Box>

      {/* Future Vehicle Assignment Section */}
      {showAutoAssignSection && (
        <Card variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Future Vehicle Assignment
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Card
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: autoAssignNewVehicles ? "primary.50" : "background.paper",
                borderColor: autoAssignNewVehicles ? "primary.main" : "divider",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoAssignNewVehicles}
                    onChange={(e) => handleAutoAssignChange(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight={500}>Automatically assign new vehicles to this permit</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Every new vehicle added to your account will be automatically assigned to this permit.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", m: 0 }}
              />
            </Card>

            <Card
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: noAutoAssignAcknowledged ? "warning.50" : "background.paper",
                borderColor: noAutoAssignAcknowledged ? "warning.main" : "divider",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noAutoAssignAcknowledged}
                    onChange={(e) => handleNoAutoAssignChange(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight={500}>Do not automatically assign new vehicles</Typography>
                    <Typography variant="body2" color="text.secondary">
                      I understand that new vehicles not assigned to a permit may result in fines or penalties.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", m: 0 }}
              />
            </Card>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default SelectVehiclesStepContent;
